import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import KpiTile from './charts/KpiTile';
import MiniBarChart from './charts/MiniBarChart';
import LiveTicker from './charts/LiveTicker';
import AnalyticsRangePanel from './AnalyticsRangePanel';
import UserOrderDetailModal from './UserOrderDetailModal';
import './UserAnalyticsOverview.css';

// User-facing analytics — mirrors the premium-fintech style of the admin
// overview but scoped to the caller's own data only.

const REFRESH_MS = 15_000;

const fmtUsd = (n) => {
  const num = Number(n);
  if (!isFinite(num)) return '—';
  if (Math.abs(num) >= 1_000) return `$${(num / 1_000).toFixed(2)}k`;
  return `$${num.toFixed(2)}`;
};
const fmtInt = (n) => Number(n || 0).toLocaleString();
const timeAgo = (d) => {
  if (!d) return '';
  const s = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60); if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60); if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

const STATUS_COLORS = {
  Completed:    '#34d399',
  'In progress':'#fbbf24',
  Processing:   '#fbbf24',
  Pending:      '#00e0ff',
  Canceled:     '#f87171',
  Refunded:     '#a78bfa',
};

const UserAnalyticsOverview = ({ onTabChange } = {}) => {
  const navigate = useNavigate();
  const goTab = (k) => { if (typeof onTabChange === 'function') onTabChange(k); };
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [openOrderId, setOpenOrderId] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const inflightRef = useRef(false);

  const load = useCallback(async () => {
    if (inflightRef.current) return;
    inflightRef.current = true;
    try {
      const res = await api.get('/user/analytics/overview');
      setData(res.data);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load analytics');
    } finally {
      inflightRef.current = false;
      setLoading(false);
    }
  }, []);

  // Pull the LATEST status for every order from the provider (the auto-poll
  // only re-reads the DB; this actually advances Pending → Completed etc.).
  const refreshOrders = useCallback(async () => {
    setRefreshing(true);
    try {
      await api.post('/smm/orders/refresh-status', {});
      await load();
    } catch (e) {
      setError(e?.response?.data?.message || 'Refresh failed');
    } finally {
      setRefreshing(false);
    }
  }, [load]);

  useEffect(() => {
    load();
    const id = setInterval(load, REFRESH_MS);
    return () => clearInterval(id);
  }, [load]);

  // Refresh instantly when a notification arrives (e.g. order status changes)
  useEffect(() => {
    const handler = () => load();
    window.addEventListener('phoneai:notification', handler);
    return () => window.removeEventListener('phoneai:notification', handler);
  }, [load]);

  if (loading && !data) {
    return (
      <div className="user-analytics user-analytics--loading">
        <div className="user-analytics-skel-grid">
          {[1, 2, 3, 4].map((i) => <div key={i} className="user-analytics-skel-tile" />)}
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="user-analytics">
        <div className="user-analytics-error">⚠ {error}</div>
      </div>
    );
  }

  if (!data) return null;
  const { kpis, series, recent_orders, recent_purchases, generated_at, profile } = data;

  const spendSeries = (series.spending_per_day_14d || []).map((p) => ({
    label: String(p.d || '').slice(5),
    value: Number(p.spend || 0),
  }));
  const totalSpend14d = spendSeries.reduce((s, p) => s + p.value, 0);

  const tickerItems = [
    { key: 'vault',  label: 'Vault',  value: kpis.vault_balance_usdt.value, suffix: 'USDT' },
    { key: 'avail',  label: 'Escrow', value: kpis.escrow_available_usdt.value, suffix: 'USDT' },
    { key: 'res',    label: 'Reserved', value: kpis.escrow_reserved_usdt.value, suffix: 'USDT' },
    { key: 'nfts',   label: 'NFTs',   value: fmtInt(kpis.nft_count.value) },
    { key: 'invested', label: 'Invested 14d', value: fmtUsd(totalSpend14d),
      accent: 'green' },
    { key: 'ord24',  label: 'Orders 24h', value: fmtInt(kpis.orders_24h.value),
      accent: kpis.orders_24h.delta_pct >= 0 ? 'green' : 'gold' },
    { key: 'spots',  label: 'Spots',
      value: `${kpis.spots_used.value}/${kpis.spots_used.total}` },
    ...(kpis.active_discount_pct.value != null
      ? [{ key: 'discount', label: 'Discount', value: `${kpis.active_discount_pct.value}%`, accent: 'amber' }]
      : []),
  ];

  return (
    <div className="user-analytics">
      <LiveTicker items={tickerItems} updatedAt={generated_at} />

      {/* ── KPI tiles — ALWAYS clickable, drill into the source ─────── */}
      <div className="user-analytics-kpi-grid">
        <KpiTile
          label="PlayVault balance"
          value={kpis.vault_balance_usdt.value}
          suffix="USDT"
          onClick={() => navigate('/play')}
          hint="Open the Play hub to use your vault"
        />
        <KpiTile
          label="SMM escrow"
          value={kpis.escrow_available_usdt.value}
          suffix="USDT"
          delta={kpis.escrow_reserved_usdt.value !== '0.00'
            ? { abs: `${kpis.escrow_reserved_usdt.value} reserved`, label: '' }
            : undefined}
          onClick={() => navigate('/store')}
          hint="Open the store to spend / top up SMM credit"
        />
        <KpiTile
          label="NFTs owned"
          value={fmtInt(kpis.nft_count.value)}
          onClick={() => goTab('nfts')}
          hint="See your NFTs"
        />
        <KpiTile
          label="Invested in growth · 14d"
          value={fmtUsd(totalSpend14d)}
          delta={kpis.orders_24h.value
            ? { abs: `${fmtInt(kpis.orders_24h.value)} orders 24h`, label: '' }
            : undefined}
          onClick={() => goTab('revenue')}
          hint="See your Growth tab — every SMM campaign you've invested in"
        />
      </div>

      {/* ── Mid: spending chart + recent orders ─────────────────────── */}
      <div className="user-analytics-mid">
        <section className="user-analytics-panel user-analytics-panel--wide">
          <div className="user-analytics-panel-head">
            <span className="user-analytics-panel-title">Your spending · last 14 days</span>
            <span className="user-analytics-panel-meta">Total {fmtUsd(totalSpend14d)}</span>
          </div>
          <MiniBarChart data={spendSeries} height={140} color="#00e0ff" />
        </section>

        <section className="user-analytics-panel">
          <div className="user-analytics-panel-head">
            <span className="user-analytics-panel-title">Your account</span>
            <span className="user-analytics-panel-meta">Active</span>
          </div>
          <dl className="user-analytics-profile">
            <div className="user-analytics-profile-row">
              <dt>Wallet</dt>
              <dd><code>{data.wallet}</code></dd>
            </div>
            <div className="user-analytics-profile-row">
              <dt>Member since</dt>
              <dd>{profile.member_since ? new Date(profile.member_since).toLocaleDateString() : '—'}</dd>
            </div>
            <div className="user-analytics-profile-row">
              <dt>Last login</dt>
              <dd>{profile.last_login ? timeAgo(profile.last_login) : '—'}</dd>
            </div>
            <div className="user-analytics-profile-row">
              <dt>Spots</dt>
              <dd>{kpis.spots_used.value} of {kpis.spots_used.total} used</dd>
            </div>
            <div className="user-analytics-profile-row">
              <dt>Open tickets</dt>
              <dd>{kpis.open_tickets.value}</dd>
            </div>
            {kpis.active_discount_pct.value != null && (
              <div className="user-analytics-profile-row">
                <dt>SMM discount</dt>
                <dd className="user-analytics-profile-discount">{kpis.active_discount_pct.value}% off</dd>
              </div>
            )}
          </dl>
        </section>
      </div>

      {/* ── Bottom: recent orders + recent purchases ────────────────── */}
      <div className="user-analytics-bottom">
        <section className="user-analytics-panel">
          <div className="user-analytics-panel-head">
            <span className="user-analytics-panel-title">Recent SMM orders</span>
            <button
              className="user-analytics-refresh"
              onClick={refreshOrders}
              disabled={refreshing}
              title="Pull the latest status for all your orders from the provider"
            >
              {refreshing ? 'Refreshing…' : '↻ Refresh all'}
            </button>
          </div>
          {recent_orders.length === 0 ? (
            <div className="user-analytics-empty">No SMM orders yet.</div>
          ) : (
            <div className="user-analytics-list">
              {recent_orders.map((o) => (
                <div
                  key={o.db_id}
                  className="user-analytics-list-row user-analytics-list-row--clickable"
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenOrderId(o.db_id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpenOrderId(o.db_id); } }}
                  title="Click for order detail"
                >
                  <span className="user-analytics-list-time">{timeAgo(o.created_at)}</span>
                  <span className="user-analytics-list-main"
                        title={o.service_name}>{o.service_name || '—'}</span>
                  <span className="user-analytics-list-meta">{fmtInt(o.quantity)} units</span>
                  <span className="user-analytics-list-amt">{fmtUsd(o.charged_usd)}</span>
                  <span
                    className="user-analytics-list-status"
                    style={{
                      color: STATUS_COLORS[o.status] || '#aaa',
                      borderColor: (STATUS_COLORS[o.status] || '#aaa') + '55',
                      background: (STATUS_COLORS[o.status] || '#aaa') + '12',
                    }}
                  >
                    {o.status || '?'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="user-analytics-panel">
          <div className="user-analytics-panel-head">
            <span className="user-analytics-panel-title">Recent store purchases</span>
            <span className="user-analytics-panel-meta">{recent_purchases.length}</span>
          </div>
          {recent_purchases.length === 0 ? (
            <div className="user-analytics-empty">
              No product purchases yet. (Your SMM service orders show on the left — this panel is only for Store products like albums &amp; beats.)
            </div>
          ) : (
            <div className="user-analytics-list">
              {recent_purchases.map((p) => (
                <div key={p.id} className="user-analytics-list-row">
                  <span className="user-analytics-list-time">{timeAgo(p.purchased_at)}</span>
                  <span className="user-analytics-list-main">{p.product_title}</span>
                  <span className="user-analytics-list-amt">{p.amount} {p.currency}</span>
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
      <AnalyticsRangePanel scope="user" />

      <UserOrderDetailModal orderId={openOrderId} onClose={() => setOpenOrderId(null)} />
    </div>
  );
};

export default UserAnalyticsOverview;
